import React from 'react';
import { useLocation, Link, Navigate } from 'react-router-dom';
import SeoHead from '@/components/layout/SeoHead';
import { ArrowRight, Check, ArrowLeft, Info, Trash2, Home, Heart, HandHeart, Refrigerator, Sofa, HardHat, TreePine } from 'lucide-react';
import { SERVICES } from '@/data/site';
import HowItWorks from '@/components/sections/HowItWorks';
import QuoteSection from '@/components/sections/QuoteSection';
import CannotTake from '@/components/sections/CannotTake';

const ICON_MAP = { Trash2, Home, Heart, HandHeart, Refrigerator, Sofa, HardHat, TreePine };

export default function ServiceDetail() {
  const location = useLocation();
  const slug = location.pathname.replace(/^\//, '').split('/')[0];
  const service = SERVICES.find((s) => s.slug === slug);

  if (!service) return <Navigate to="/services" replace />;

  const Ico = ICON_MAP[service.icon];

  return (
    <>
      <SeoHead
        title={`${service.title} | Ohana Property Services`}
        description={service.short}
        canonical={`https://ohanapropertyexperts.com/${service.slug}`}
      />

      <section className="bg-ohana-primary text-ohana-bg">
        <div className="container-ohana pt-24 pb-20 md:pt-32 md:pb-28">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-sm text-ohana-bg/70 hover:text-white transition mb-8"
            data-testid="service-back-link"
          >
            <ArrowLeft className="h-4 w-4" />
            All Services
          </Link>
          <div className="flex items-center gap-4">
            <span className="grid h-14 w-14 place-items-center rounded-2xl bg-ohana-accent text-white">
              {Ico && <Ico className="h-7 w-7" />}
            </span>
            <p className="overline !text-ohana-accent">{service.title}</p>
          </div>
          <h1 className="mt-6 font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] max-w-4xl">
            {service.hero}
          </h1>
          <p className="mt-6 text-lg md:text-xl text-ohana-bg/80 max-w-2xl">
            {service.description}
          </p>
          <div className="mt-10">
            <Link to="/contact" className="btn-primary" data-testid="service-detail-cta">
              Get a Free Quote
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="section-pad bg-ohana-bg">
        <div className="container-ohana grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <p className="overline">Who It's For</p>
            <h2 className="h-display text-3xl md:text-4xl mt-3 leading-tight">
              Made for situations like these.
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-ohana-text">{service.forWho}</p>

            <div className="mt-10">
              <p className="overline">What's Included</p>
              <ul className="mt-5 space-y-3">
                {service.items.map((it) => (
                  <li key={it} className="flex items-start gap-3" data-testid={`service-item-${it.toLowerCase().replace(/[^a-z]+/g, '-').slice(0, 30)}`}>
                    <span className="mt-0.5 grid h-6 w-6 flex-none place-items-center rounded-full bg-ohana-accent/15 text-ohana-accent">
                      <Check className="h-4 w-4" />
                    </span>
                    <span className="text-ohana-text leading-relaxed">{it}</span>
                  </li>
                ))}
              </ul>
            </div>

            {service.addonNote && (
              <div
                className="mt-10 rounded-2xl border border-ohana-accent/30 bg-ohana-accent/5 p-6 flex gap-4"
                data-testid="service-addon-note"
              >
                <span className="mt-0.5 grid h-9 w-9 flex-none place-items-center rounded-full bg-ohana-accent/15 text-ohana-accent">
                  <Info className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-ohana-accent">
                    Add-On Available
                  </p>
                  <p className="mt-2 text-ohana-text leading-relaxed">{service.addonNote}</p>
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-5">
            <div className="sticky top-24 rounded-3xl bg-ohana-surface border border-ohana-border p-8">
              <p className="overline">Pricing Guidance</p>
              <p className="mt-3 font-serif text-2xl md:text-3xl text-ohana-primary leading-snug">
                {service.pricingNote}
              </p>
              <p className="mt-5 text-sm text-ohana-text-muted leading-relaxed">
                Final pricing is confirmed after photos. We give you an exact number, not a range, before we touch anything.
              </p>
              <Link
                to="/contact"
                className="btn-primary mt-7 w-full"
                data-testid="service-sidebar-cta"
              >
                Send Photos & Get a Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      <HowItWorks />

      {service.slug === 'junk-removal' && <CannotTake />}

      <section className="section-pad bg-ohana-surface" data-testid="other-services">
        <div className="container-ohana">
          <p className="overline">Explore More</p>
          <h2 className="h-display text-3xl md:text-4xl mt-3 leading-tight">
            Other ways we can help.
          </h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICES.filter((s) => s.slug !== service.slug)
              .slice(0, 3)
              .map((s) => {
                const I = ICON_MAP[s.icon];
                return (
                  <Link
                    key={s.slug}
                    to={`/${s.slug}`}
                    className="group rounded-2xl bg-white border border-ohana-border p-6 transition hover:-translate-y-1 hover:shadow-xl hover:shadow-ohana-primary/5"
                    data-testid={`other-service-${s.slug}`}
                  >
                    <span className="grid h-10 w-10 place-items-center rounded-lg bg-ohana-surface text-ohana-primary">
                      {I && <I className="h-5 w-5" />}
                    </span>
                    <h3 className="mt-4 font-serif text-xl text-ohana-primary leading-snug">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-sm text-ohana-text-muted leading-relaxed">{s.short}</p>
                    <span className="mt-4 inline-flex items-center text-sm font-bold text-ohana-accent group-hover:translate-x-1 transition-transform">
                      Learn more <ArrowRight className="ml-1 h-4 w-4" />
                    </span>
                  </Link>
                );
              })}
          </div>
        </div>
      </section>

      <QuoteSection defaultJobType={service.title} />
    </>
  );
}
